import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  EmptyState,
  FilterBar,
  FilterSelect,
  InlineStats,
  Pagination,
  StatusPill,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useToast } from '../../components/admin/overlays';
import { IconCard, IconRefresh } from '../../components/admin/icons';
import {
  formatDateTime,
  formatNumber,
  transactionTypeLabels,
  transactionTypeTone
} from '../../components/admin/labels';

const typeOptions = Object.keys(transactionTypeLabels);

const PER_PAGE = 25;

/** Which side of the business a row belongs to — the ledger holds both. */
const KIND_LABELS = { merchant: 'Merchant', courier: 'Courier' };

/**
 * The payment ledger. Every row here was written by a Stripe webhook the
 * moment it confirmed a charge, so this is a record of what was actually
 * paid, not what was requested (that's Invoice).
 *
 * Merchants and couriers share it: the two bill for different things, but
 * "money ParcelBuddy took" is one question, so it gets one list with an
 * account column that knows which kind it is linking to.
 */
const TransactionManagement = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // Filters live in the URL so a filtered ledger can be linked to, reloaded
  // and backed out of — the same treatment the public pricing tabs get.
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = {
    accountKind: searchParams.get('kind') || '',
    type: searchParams.get('type') || '',
    page: Math.max(1, Number(searchParams.get('page')) || 1)
  };
  const setFilter = (patch) => {
    const next = { ...filters, ...patch };
    // Any filter change resets to page one: page 4 of the old result set is
    // rarely page 4 of the new one.
    if (!('page' in patch)) next.page = 1;
    const params = {};
    if (next.accountKind) params.kind = next.accountKind;
    if (next.type) params.type = next.type;
    if (next.page > 1) params.page = String(next.page);
    setSearchParams(params, { replace: true });
  };

  const [transactions, setTransactions] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1, totalAmount: 0 });
  const [loading, setLoading] = useState(true);

  const { accountKind, type, page } = filters;

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (accountKind) params.append('accountKind', accountKind);
      if (type) params.append('type', type);
      params.append('page', page);
      params.append('limit', PER_PAGE);

      const { data } = await api.get(`/api/admin/transactions?${params}`);
      setTransactions(data.transactions || []);
      setMeta({
        total: data.total || 0,
        totalPages: data.totalPages || 1,
        totalAmount: data.totalAmount || 0
      });
    } catch (error) {
      toast('Could not load transactions.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [accountKind, type, page, toast]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const activeFilterCount = [accountKind, type].filter(Boolean).length;

  /**
   * Where this row's account lives in admin. Merchants have a detail page of
   * their own; courier accounts are managed from a list, so the link lands
   * there pre-searched for this one rather than on an unfiltered table.
   */
  const openAccount = (t) => {
    if (!t.account?._id) return;
    navigate(
      t.accountKind === 'courier'
        ? `/admin/courier-accounts?q=${encodeURIComponent(t.account.email || t.account.name || '')}`
        : `/admin/merchants/${t.account._id}`
    );
  };

  return (
    // No card header: the admin topbar already names this page and says what
    // it is, so a second title inside the card was height spent repeating it.
    <Card>
      <FilterBar
        actions={
          <>
            <InlineStats
              items={[
                { label: 'Payments', value: formatNumber(meta.total) },
                { label: 'Value', value: `£${Number(meta.totalAmount).toFixed(2)}` }
              ]}
            />
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSearchParams({}, { replace: true })}>
                Clear
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={fetchTransactions} loading={loading}>
              <IconRefresh size={15} />
              Refresh
            </Button>
          </>
        }
      >
        <FilterSelect
          label="Account"
          value={accountKind}
          onChange={(e) => setFilter({ accountKind: e.target.value })}
        >
          <option value="">All</option>
          <option value="merchant">Merchants</option>
          <option value="courier">Couriers</option>
        </FilterSelect>

        <FilterSelect label="Type" value={type} onChange={(e) => setFilter({ type: e.target.value })}>
          <option value="">All types</option>
          {typeOptions.map((key) => (
            <option key={key} value={key}>
              {transactionTypeLabels[key]}
            </option>
          ))}
        </FilterSelect>
      </FilterBar>

      {loading ? (
        <TableSkeleton rows={8} cols={6} />
      ) : transactions.length === 0 ? (
        <EmptyState
          icon={IconCard}
          title={activeFilterCount ? 'No transactions match these filters' : 'No transactions yet'}
          description={
            activeFilterCount
              ? 'Try clearing one or more filters to widen the search.'
              : 'Subscription signups, renewals and per-case payments appear here as they happen.'
          }
          action={
            activeFilterCount ? (
              <Button variant="secondary" size="sm" onClick={() => setSearchParams({}, { replace: true })}>
                Clear filters
              </Button>
            ) : null
          }
        />
      ) : (
        <TableWrap>
          <thead>
            <tr>
              <Th>Account</Th>
              <Th>Type</Th>
              <Th className="text-right">Amount</Th>
              <Th>Plan</Th>
              <Th>Description</Th>
              <Th>Date</Th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((t) => (
              <Tr key={t._id}>
                <Td>
                  {t.account ? (
                    <button
                      type="button"
                      onClick={() => openAccount(t)}
                      className="rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                    >
                      <span className="flex items-center gap-2">
                        <span className="font-600 text-brand-700 hover:underline">
                          {t.account.name}
                        </span>
                        {/* The kind is on every row, not just courier ones:
                            "no badge" would be an easy thing to misread as
                            "not loaded yet". */}
                        <StatusPill
                          tone={t.accountKind === 'courier' ? 'info' : 'neutral'}
                          dot={false}
                        >
                          {KIND_LABELS[t.accountKind] || t.accountKind}
                        </StatusPill>
                      </span>
                      <span className="block text-xs text-ink-500">{t.account.email}</span>
                    </button>
                  ) : (
                    <span className="text-ink-500">—</span>
                  )}
                </Td>
                <Td>
                  <StatusPill tone={transactionTypeTone[t.type] || 'neutral'}>
                    {transactionTypeLabels[t.type] || t.type}
                  </StatusPill>
                </Td>
                <Td className="whitespace-nowrap text-right font-600 tabular-nums text-ink-900">
                  £{Number(t.amount || 0).toFixed(2)}
                </Td>
                <Td>{t.plan ? t.plan.charAt(0).toUpperCase() + t.plan.slice(1) : '—'}</Td>
                <Td className="max-w-[18rem]">
                  <span className="block truncate">{t.description || '—'}</span>
                  {t.case?.caseId && <span className="block text-xs text-ink-500">{t.case.caseId}</span>}
                </Td>
                <Td className="whitespace-nowrap text-ink-500">{formatDateTime(t.occurredAt)}</Td>
              </Tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      {!loading && (
        <Pagination
          page={page}
          totalPages={meta.totalPages}
          total={meta.total}
          perPage={PER_PAGE}
          shown={transactions.length}
          onPage={(next) => setFilter({ page: next })}
        />
      )}
    </Card>
  );
};

export default TransactionManagement;
